/**
 * Supplier Payment enums and display labels for the Rebates module
 *
 * These are shared between backend and frontend to ensure consistency.
 */

/**
 * Types of supplier payments for rebates
 */
export enum RebateSupplierPaymentType {
  CHECK = "check",
  WIRE_TRANSFER = "wire_transfer",
  CREDIT_NOTE = "credit_note",
  AP_DEDUCTION = "ap_deduction",
  DEBIT = "debit",
  INTERNAL_TRANSFER = "internal_transfer",
}

/**
 * Payment types that can be manually created by users.
 * DEBIT is excluded because it is auto-generated by the forecasting system.
 */
export const MANUAL_PAYMENT_TYPES = [
  RebateSupplierPaymentType.CHECK,
  RebateSupplierPaymentType.WIRE_TRANSFER,
  RebateSupplierPaymentType.CREDIT_NOTE,
  RebateSupplierPaymentType.AP_DEDUCTION,
  RebateSupplierPaymentType.INTERNAL_TRANSFER,
] as const;

/**
 * Display labels for RebateSupplierPaymentType
 */
export const REBATE_SUPPLIER_PAYMENT_TYPE_LABELS: Record<
  RebateSupplierPaymentType,
  string
> = {
  [RebateSupplierPaymentType.CHECK]: "Check",
  [RebateSupplierPaymentType.WIRE_TRANSFER]: "Wire Transfer",
  [RebateSupplierPaymentType.CREDIT_NOTE]: "Credit Note",
  [RebateSupplierPaymentType.AP_DEDUCTION]: "AP Deduction",
  [RebateSupplierPaymentType.DEBIT]: "Debit (Auto-Generated)",
  [RebateSupplierPaymentType.INTERNAL_TRANSFER]: "Internal Transfer",
};

/**
 * Types of payment allocations
 */
export enum RebateSupplierPaymentAllocationType {
  MANUAL = "manual",
  AUTO = "auto",
  REMITTANCE_BASED = "remittance_based",
}

/**
 * Display labels for RebateSupplierPaymentAllocationType
 */
export const REBATE_SUPPLIER_PAYMENT_ALLOCATION_TYPE_LABELS: Record<
  RebateSupplierPaymentAllocationType,
  string
> = {
  [RebateSupplierPaymentAllocationType.MANUAL]: "Manual",
  [RebateSupplierPaymentAllocationType.AUTO]: "Auto",
  [RebateSupplierPaymentAllocationType.REMITTANCE_BASED]: "Remittance Based",
};

/**
 * Types of debit adjustment allocations.
 * Debit adjustments reduce the expected rebate amount for specific cells
 * (rule/division/month) based on negative forecast breakdowns (e.g., returns).
 */
export enum RebateDebitAdjustmentAllocationType {
  AUTO = "auto",
  MANUAL = "manual",
}

/**
 * Display labels for RebateDebitAdjustmentAllocationType
 */
export const REBATE_DEBIT_ADJUSTMENT_ALLOCATION_TYPE_LABELS: Record<
  RebateDebitAdjustmentAllocationType,
  string
> = {
  [RebateDebitAdjustmentAllocationType.AUTO]: "Auto",
  [RebateDebitAdjustmentAllocationType.MANUAL]: "Manual",
};
